import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';

export interface PasswordStrengthErrors {
  minLength?: boolean;
  uppercase?: boolean;
  lowercase?: boolean;
  digit?: boolean;
  specialChar?: boolean;
}

export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
  strength: number; // 0-5
  strengthLabel: string;
  strengthClass: string;
}

@Injectable({
  providedIn: 'root',
})
export class PasswordValidationService {
  private readonly PASSWORD_MIN_LENGTH = 12;
  private readonly SPECIAL_CHAR_REGEX = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/;

  /**
   * Validateur de force du mot de passe (pour FormControl)
   */
  passwordStrengthValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;

      const errors: PasswordStrengthErrors = {};

      if (value.length < this.PASSWORD_MIN_LENGTH) {
        errors.minLength = true;
      }
      if (!/[A-Z]/.test(value)) {
        errors.uppercase = true;
      }
      if (!/[a-z]/.test(value)) {
        errors.lowercase = true;
      }
      if (!/[0-9]/.test(value)) {
        errors.digit = true;
      }
      if (!this.SPECIAL_CHAR_REGEX.test(value)) {
        errors.specialChar = true;
      }

      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  /**
   * Validateur de correspondance des mots de passe (pour FormGroup)
   * @param passwordField Nom du champ mot de passe
   * @param confirmPasswordField Nom du champ confirmation
   */
  passwordMatchValidator(
    passwordField = 'password',
    confirmPasswordField = 'confirmPassword'
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const password = control.get(passwordField);
      const confirmPassword = control.get(confirmPasswordField);

      if (!password || !confirmPassword || confirmPassword.value === '') {
        return null;
      }

      return password.value === confirmPassword.value
        ? null
        : { passwordMismatch: true };
    };
  }

  /**
   * Validateur pour vérifier que deux mots de passe sont différents
   * @param field1 Premier champ
   * @param field2 Deuxième champ
   */
  passwordDifferenceValidator(field1: string, field2: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const password1 = control.get(field1);
      const password2 = control.get(field2);

      if (!password1?.value || !password2?.value) {
        return null;
      }

      return password1.value === password2.value
        ? { samePassword: true }
        : null;
    };
  }

  /**
   * Obtenir les erreurs de validation sous forme de tableau de messages
   */
  getPasswordErrors(control: AbstractControl | null): string[] {
    if (!control || !control.errors) return [];

    const errors: string[] = [];

    if (control.errors['required']) {
      return ['Le mot de passe est obligatoire'];
    }

    if (control.errors['minLength']) {
      errors.push(`Au moins ${this.PASSWORD_MIN_LENGTH} caractères`);
    }
    if (control.errors['uppercase']) {
      errors.push('Au moins une majuscule');
    }
    if (control.errors['lowercase']) {
      errors.push('Au moins une minuscule');
    }
    if (control.errors['digit']) {
      errors.push('Au moins un chiffre');
    }
    if (control.errors['specialChar']) {
      errors.push('Au moins un caractère spécial (!@#$%...)');
    }

    return errors;
  }

  /**
   * Calculer la force du mot de passe (0-5)
   */
  calculatePasswordStrength(control: AbstractControl | null): number {
    if (!control?.value) return 0;

    const errors = control.errors;
    const errorsCount = errors ? Object.keys(errors).length : 0;

    return 5 - errorsCount;
  }

  /**
   * Obtenir le résultat complet de validation
   */
  getPasswordValidationResult(
    control: AbstractControl | null
  ): PasswordValidationResult {
    const strength = this.calculatePasswordStrength(control);
    const errors = this.getPasswordErrors(control);

    return {
      isValid: errors.length === 0 && !!control?.value,
      errors,
      strength,
      strengthLabel: this.getStrengthLabel(strength),
      strengthClass: this.getStrengthClass(strength),
    };
  }

  /**
   * Obtenir la classe CSS pour la barre de force (index-based)
   */
  getPasswordStrengthBarClass(
    control: AbstractControl | null,
    barIndex: number
  ): string {
    const strength = this.calculatePasswordStrength(control);

    if (!control?.value) return 'bg-gray-200';

    if (barIndex <= strength) {
      if (strength <= 2) return 'bg-red-500';
      if (strength === 3) return 'bg-orange-500';
      if (strength === 4) return 'bg-yellow-500';
      return 'bg-green-500';
    }

    return 'bg-gray-200';
  }

  /**
   * Obtenir le label de force
   */
  private getStrengthLabel(strength: number): string {
    if (strength === 0) return 'users.password_strength_none';
    if (strength <= 2) return 'users.password_strength_weak';
    if (strength === 3) return 'users.password_strength_medium';
    if (strength === 4) return 'users.password_strength_good';
    return 'users.password_strength_strong';
  }

  /**
   * Obtenir la classe CSS globale
   */
  private getStrengthClass(strength: number): string {
    if (strength === 0) return 'text-gray-400';
    if (strength <= 2) return 'text-red-500';
    if (strength === 3) return 'text-orange-500';
    if (strength === 4) return 'text-yellow-500';
    return 'text-green-500';
  }

  /**
   * Vérifier si un champ a une erreur spécifique et est touché
   */
  hasFieldError(
    form: FormGroup,
    fieldName: string,
    errorType: string
  ): boolean {
    const control = form.get(fieldName);
    return !!(control && control.hasError(errorType) && control.touched);
  }

  /**
   * Vérifier si le formulaire a une erreur au niveau groupe
   */
  hasFormError(
    form: FormGroup,
    errorType: string,
    fieldName?: string
  ): boolean {
    const hasError = form.hasError(errorType);

    if (!fieldName) return hasError;

    const field = form.get(fieldName);
    return !!(hasError && field?.touched);
  }
}
